import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { countries } from "countries-list";

let countryNameCodeMap = {};
Object.keys(countries).forEach((key) => {
  countryNameCodeMap[key] = countries[key].name;
});

const useGroupSelector = (allGroups = [], onChange, selectedGroups = null) => {
  const [selectionType, setSelectionType] = useState(
    selectedGroups?.selectionType || "all"
  );
  const [selectedOptions, setSelectedOptions] = useState(
    selectedGroups?.selectedValues || []
  );

  const prevSelectedGroupsRef = useRef(null);

  useEffect(() => {
    if (selectedGroups && allGroups.length > 0) {
      const type = selectedGroups.selectionType || "all";
      const values = selectedGroups.selectedValues || [];
      const groupIds = selectedGroups.groupIds || ["All"];

      // Check if selectedGroups actually changed to prevent infinite loop
      const prev = prevSelectedGroupsRef.current;
      const hasChanged =
        !prev ||
        prev.selectionType !== type ||
        JSON.stringify(prev.selectedValues || []) !== JSON.stringify(values) ||
        JSON.stringify(prev.groupIds || []) !== JSON.stringify(groupIds);

      if (hasChanged) {
        setSelectionType(type);
        setSelectedOptions(values);
        prevSelectedGroupsRef.current = {
          selectionType: type,
          selectedValues: values,
          groupIds,
        };
      }
    } else if (!selectedGroups) {
      // Reset ref when selectedGroups is null
      prevSelectedGroupsRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups, allGroups.length]);


  const calculateResult = useCallback(
    (type, options) => {
      if (type === "all") {
        return ["All"];
      }

      if (type === "official") {
        return ["Official Groups"];
      }

      if (type === "region" || type === "country") {
        return [];
      }

      // type === "group"
      const groupIds = new Set();
      options.forEach((value) => {
        const group = allGroups.find((g) => g.id === value);
        if (group) {
          groupIds.add(group.id);
        }
      });

      return Array.from(groupIds);
    },
    [allGroups]
  );

  const handleSelectionTypeChange = useCallback(
    (newType) => {
      setSelectionType(newType);

      setSelectedOptions([]);

      if (onChange) {
        const groupIds = calculateResult(newType, []);
        onChange({
          selectionType: newType,
          selectedValues: [],
          groupIds,
        });
      }
    },
    [onChange, calculateResult]
  );

  const handleSelectedOptionsChange = useCallback(
    (newOptions) => {
      setSelectedOptions(newOptions);

      if (onChange) {
        const groupIds = calculateResult(selectionType, newOptions);
        onChange({
          selectionType,
          selectedValues: newOptions,
          groupIds,
        });
      }
    },
    [onChange, selectionType, calculateResult]
  );

  useEffect(() => {
    if (allGroups.length > 0 && onChange && !selectedGroups) {
      onChange({
        selectionType: "all",
        selectedValues: [],
        groupIds: ["All"],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups.length]);

  const displayInfo = useMemo(() => {
    const result = calculateResult(selectionType, selectedOptions);

    if (result[0] === "All") {
      return {
        type: "all",
        count: allGroups.length,
        label: `All groups (${allGroups.length})`,
      };
    }

    if (result[0] === "Official Groups") {
      const officialCount = allGroups.filter(
        (g) => g.groupType === "Official Supporters Group"
      ).length;
      return {
        type: "official",
        count: officialCount,
        label: `Official groups (${officialCount})`,
      };
    }

    return {
      type: "custom",
      count: result.length,
      label: `${result.length} group(s) selected`,
    };
  }, [selectionType, selectedOptions, calculateResult, allGroups]);

  return {
    selectionType,
    selectedOptions,
    displayInfo,
    handleSelectionTypeChange,
    handleSelectedOptionsChange,
  };
};

export default useGroupSelector;
